/**
 * D-EZ-109 — THE DISPATCH POWER RANKINGS. Commentary only; it changes nothing.
 *
 * ===========================================================================
 * THE FORMULA (tested in `wire.test.ts`; change the numbers here and re-measure)
 * ===========================================================================
 *
 *   finish score   f(p) = (field + 1 − p) / field       1.00 for a win, ≈0.02 for last;
 *                                                       0 for a race he did not start
 *   win bonus      +0.25 on a race he won
 *   form           the season's last five races (fewer early on), most recent
 *                  weighted 5, then 4, 3, 2, 1:
 *                    form = Σ wₖ·(f + bonus) / Σ wₖ           (0 … 1.25)
 *   points         share = his points / the leader's points   (0 … 1)
 *
 *   POWER = 60 × form + 40 × share
 *
 * Ties go to more wins, then more points, then the better last finish, then
 * the driver id (so a tie never flickers). Ranked: every driver who has started
 * a race this season and is not lost to the season (dead, or out for the year).
 *
 * Movement is against last week's ranking, built the same way through race
 * N−1. The one-line reason is the most telling fact about his week —
 * a win, a streak, the points lead, a big jump or fall — with a penalty on any
 * kind of reason he was given in the last three weeks, so a man who sits at
 * No. 1 for a month reads four different lines.
 */

import type { HistorySeason } from '../history/source.ts';
import type { SeasonRaceResult } from '../seasonPlay.ts';
import type { SeasonStandings } from '../standings.ts';
import { shortTrack } from '../paper/fallbackPaper.ts';
import { stableHash } from '../story/hometown.ts';
import { num, say, type WireLine } from './line.ts';

export const POWER_FORMULA = {
  window: 5,
  weights: [5, 4, 3, 2, 1] as readonly number[],
  winBonus: 0.25,
  formWeight: 60,
  pointsWeight: 40,
  top: 10,
  /** How far back a reason kind counts against him, and by how much. */
  memoryWeeks: 3,
  repeatPenalty: 40,
} as const;

/** f(p) = (field + 1 − p) / field. */
export function finishScore(place: number, field: number): number {
  return field > 0 ? Math.max(0, (field + 1 - place) / field) : 0;
}

export interface PowerScore {
  driverId: string;
  name: string;
  power: number;
  form: number;
  share: number;
  points: number;
  wins: number;
  /** His finish in race N, or null when he did not start. */
  lastFinish: number | null;
}

/** The season's races through `n`, oldest first. */
function racesThrough(season: HistorySeason, n: number): SeasonRaceResult[] {
  return season.season.races.filter((r) => r.raceNumber <= n).sort((a, b) => a.raceNumber - b.raceNumber);
}

function standingsAfter(races: readonly SeasonRaceResult[], n: number): SeasonStandings | null {
  const r = races.find((x) => x.raceNumber === n);
  return r && r.standings && r.standings.drivers.length > 0 ? r.standings : null;
}

/**
 * The power scores after race `n`, best first. `lost` are drivers who cannot
 * race again this season (dead, out for the year) and are left off.
 */
export function powerScores(season: HistorySeason, n: number, lost: ReadonlySet<string> = new Set()): PowerScore[] {
  const races = racesThrough(season, n);
  const table = standingsAfter(races, n);
  if (!table) return [];
  const window = races.slice(-POWER_FORMULA.window).reverse(); // most recent first
  const weights = POWER_FORMULA.weights.slice(0, window.length);
  const wsum = weights.reduce((a, b) => a + b, 0);
  const leaderPts = table.drivers[0]?.points ?? 0;
  const out: PowerScore[] = [];
  for (const d of table.drivers) {
    if (d.starts <= 0 || lost.has(d.driverId)) continue;
    let form = 0;
    window.forEach((r, k) => {
      const f = r.race.finishingOrder.find((x) => x.driverId === d.driverId);
      if (!f) return;
      const field = r.race.finishingOrder.length;
      form += weights[k] * (finishScore(f.place, field) + (r.race.winnerId === d.driverId ? POWER_FORMULA.winBonus : 0));
    });
    form = wsum > 0 ? form / wsum : 0;
    const share = leaderPts > 0 ? d.points / leaderPts : 0;
    const last = window[0]?.race.finishingOrder.find((x) => x.driverId === d.driverId)?.place ?? null;
    out.push({
      driverId: d.driverId,
      name: d.name,
      power: POWER_FORMULA.formWeight * form + POWER_FORMULA.pointsWeight * share,
      form,
      share,
      points: d.points,
      wins: d.wins,
      lastFinish: last,
    });
  }
  out.sort(
    (a, b) =>
      b.power - a.power ||
      b.wins - a.wins ||
      b.points - a.points ||
      (a.lastFinish ?? 999) - (b.lastFinish ?? 999) ||
      a.driverId.localeCompare(b.driverId),
  );
  return out;
}

// ---------------------------------------------------------------------------
// The reasons
// ---------------------------------------------------------------------------

export interface PowerRow {
  rank: number;
  driverId: string;
  name: string;
  power: number;
  /** Last week's rank, or null when he was not ranked (or there was no last week). */
  prevRank: number | null;
  /** Places gained (+) or lost (−); null = new to the top ten (or week one). */
  move: number | null;
  reason: WireLine;
}

export interface PowerWeek {
  raceNumber: number;
  rows: PowerRow[];
}

interface Candidate {
  kind: string;
  salience: number;
  templates: readonly string[];
  slots: Record<string, string | ReturnType<typeof num> | null>;
}

const T: Record<string, readonly string[]> = {
  won: [
    'Won at {track}.',
    'Took the checkered flag at {track}.',
    'The {track} winner.',
    'Went to victory lane at {track}.',
  ],
  'won-deep': [
    'Won at {track} from {start} on the grid.',
    'Came from {start} to win at {track}.',
    'The {track} winner, all the way from {start}.',
    'Started {start}, finished first at {track}.',
  ],
  'win-streak': [
    '{streak} straight wins — nobody can touch him right now.',
    'Has won {streak} in a row.',
    'A {streak}-race winning streak and counting.',
  ],
  'window-wins': [
    'Won {wins} of the last {window}.',
    '{wins} wins in the last {window} races.',
    'Has been to victory lane {wins} times in {window} weeks.',
  ],
  leader: [
    'Leads the points by {gap}.',
    'Holds the points lead, {gap} clear.',
    'On top of the table by {gap} points.',
    'Points leader — {gap} to the good.',
  ],
  'top5-streak': [
    '{streak} straight top-fives.',
    'Top five in each of his last {streak}.',
    'Has not finished outside the top five in {streak} races.',
  ],
  climb: [
    'Up {jump} spots after a {finish} at {track}.',
    'Jumps {jump} on the strength of a {finish} at {track}.',
    'A {finish} at {track} moves him up {jump}.',
  ],
  second: [
    'Runner-up at {track}.',
    'Second at {track}, close but not quite.',
    'Ran second at {track}.',
  ],
  'season-wins': [
    '{wins} wins on the year.',
    'Already has {wins} wins this season.',
    'A {wins}-win season so far.',
  ],
  slide: [
    'Slides {drop} after a {finish} at {track}; still {rank} in points.',
    'A {finish} at {track} costs him {drop} spots.',
    'Down {drop} — {track} ({finish}) was a day to forget.',
  ],
  'out-early': [
    'Out of the race early at {track}, but {rank} in points keeps him here.',
    'A DNF at {track} hurts; {rank} in the standings holds him up.',
  ],
  'avg-finish': [
    'Averaging {avg} over his last {window} starts.',
    'An average finish of {avg} in his last {window}.',
    '{avg} average finish lately — steady.',
  ],
  'top10-streak': [
    '{streak} top-tens in a row.',
    'Top ten in his last {streak} starts.',
  ],
  'good-run': [
    '{finish} at {track}.',
    'A solid {finish} at {track}.',
    'Brought it home {finish} at {track}.',
  ],
  'winless-top': [
    'Still looking for his first win of the year, but {rank} in points.',
    'No wins yet, {rank} in points anyway.',
    'Winless, but sitting {rank} in the standings.',
  ],
  points: [
    '{rank} in points, {behind} back.',
    'Sits {rank} in the standings, {behind} off the lead.',
    '{behind} points behind the leader in {rank}.',
  ],
};

interface WeekFacts {
  raceNumber: number;
  track: string;
  races: SeasonRaceResult[];
  table: SeasonStandings;
}

function candidatesFor(id: string, f: WeekFacts, move: number | null): Candidate[] {
  const out: Candidate[] = [];
  const run = f.races[f.races.length - 1];
  const me = run.race.finishingOrder.find((x) => x.driverId === id) ?? null;
  const stat = f.table.byDriver[id];
  if (!stat) return out;
  const start = run.startPositions[id];
  const won = run.race.winnerId === id;
  const leader = f.table.drivers[0];
  const second = f.table.drivers[1];
  const rankOrd = num(stat.rank, 'ord');

  if (won) {
    const deep = start !== undefined && start >= 10;
    out.push({
      kind: 'won',
      salience: 100,
      templates: deep ? T['won-deep'] : T.won,
      slots: { track: f.track, start: deep ? num(start, 'ord') : null },
    });
  }
  // Win streak, counted back from race N through the races he started.
  let streak = 0;
  for (let i = f.races.length - 1; i >= 0 && f.races[i].race.winnerId === id; i--) streak++;
  if (streak >= 2) out.push({ kind: 'win-streak', salience: 97, templates: T['win-streak'], slots: { streak: num(streak, 'word') } });

  const window = f.races.slice(-POWER_FORMULA.window);
  const wWins = window.filter((r) => r.race.winnerId === id).length;
  if (wWins >= 2 && streak < 2) out.push({ kind: 'window-wins', salience: 85, templates: T['window-wins'], slots: { wins: num(wWins, 'word'), window: num(window.length, 'word') } });

  if (leader?.driverId === id && second) {
    out.push({ kind: 'leader', salience: 80, templates: T.leader, slots: { gap: num(leader.points - second.points) } });
  }
  let t5 = 0;
  for (let i = f.races.length - 1; i >= 0; i--) {
    const p = f.races[i].race.finishingOrder.find((x) => x.driverId === id)?.place;
    if (p === undefined || p > 5) break;
    t5++;
  }
  if (t5 >= 3) out.push({ kind: 'top5-streak', salience: 72, templates: T['top5-streak'], slots: { streak: num(t5, 'word') } });

  if (move !== null && move >= 3 && me) {
    out.push({ kind: 'climb', salience: 66, templates: T.climb, slots: { jump: num(move, 'word'), finish: num(me.place, 'ord'), track: f.track } });
  }
  if (me && me.place === 2) out.push({ kind: 'second', salience: 62, templates: T.second, slots: { track: f.track } });
  if (stat.wins >= 2) out.push({ kind: 'season-wins', salience: 55, templates: T['season-wins'], slots: { wins: num(stat.wins, 'word') } });
  if (move !== null && move <= -3 && me) {
    out.push({ kind: 'slide', salience: 58, templates: T.slide, slots: { drop: num(-move, 'word'), finish: num(me.place, 'ord'), track: f.track, rank: rankOrd } });
  }
  if (me && me.status !== 'running') {
    out.push({ kind: 'out-early', salience: 52, templates: T['out-early'], slots: { track: f.track, rank: rankOrd } });
  }
  const finishes = window.map((r) => r.race.finishingOrder.find((x) => x.driverId === id)?.place).filter((p): p is number => p !== undefined);
  if (finishes.length >= 3) {
    const avg = finishes.reduce((a, b) => a + b, 0) / finishes.length;
    if (avg <= 12) out.push({ kind: 'avg-finish', salience: 42, templates: T['avg-finish'], slots: { avg: num(Math.round(avg * 10) / 10, 'dec1'), window: num(finishes.length, 'word') } });
  }
  let t10 = 0;
  for (let i = f.races.length - 1; i >= 0; i--) {
    const p = f.races[i].race.finishingOrder.find((x) => x.driverId === id)?.place;
    if (p === undefined || p > 10) break;
    t10++;
  }
  if (t10 >= 4 && t5 < 3) out.push({ kind: 'top10-streak', salience: 45, templates: T['top10-streak'], slots: { streak: num(t10, 'word') } });
  if (me && me.place >= 3 && me.place <= 10 && !won) out.push({ kind: 'good-run', salience: 38, templates: T['good-run'], slots: { finish: num(me.place, 'ord'), track: f.track } });
  if (stat.wins === 0 && stat.rank <= 12 && f.races.length >= 4) {
    out.push({ kind: 'winless-top', salience: 34, templates: T['winless-top'], slots: { rank: rankOrd } });
  }
  if (leader && leader.driverId !== id) {
    out.push({ kind: 'points', salience: 20, templates: T.points, slots: { rank: rankOrd, behind: num(leader.points - stat.points) } });
  }
  return out;
}

/**
 * The rankings for every week of a season through race `n`, oldest first —
 * one forward pass, so each week knows the movement and the reasons of the
 * week before it, and nothing after its own race.
 */
export function powerWeeks(
  season: HistorySeason,
  n: number,
  opts: { key: string; lostAfter?: (raceNumber: number) => ReadonlySet<string> },
): PowerWeek[] {
  const all = racesThrough(season, n);
  const weeks: PowerWeek[] = [];
  const recent = new Map<string, { kind: string; week: number }[]>();
  const used = new Map<string, number>(); // `${id}|${kind}` → times this season
  let prev: Map<string, number> | null = null;
  for (let i = 0; i < all.length; i++) {
    const races = all.slice(0, i + 1);
    const run = races[races.length - 1];
    const table = standingsAfter(races, run.raceNumber);
    if (!table) continue;
    const scores = powerScores(season, run.raceNumber, opts.lostAfter?.(run.raceNumber)).slice(0, POWER_FORMULA.top);
    const facts: WeekFacts = { raceNumber: run.raceNumber, track: shortTrack(run.scheduled.trackName), races, table };
    const rows: PowerRow[] = scores.map((s, k) => {
      const prevRank = prev?.get(s.driverId) ?? null;
      const move = prev === null ? null : prevRank === null ? null : prevRank - (k + 1);
      const mine = recent.get(s.driverId) ?? [];
      const cands = candidatesFor(s.driverId, facts, prev === null ? null : prevRank === null ? null : move).map((c) => ({
        ...c,
        salience:
          c.salience -
          POWER_FORMULA.repeatPenalty * mine.filter((m) => m.kind === c.kind && run.raceNumber - m.week <= POWER_FORMULA.memoryWeeks).length +
          // A hair of stable jitter so two equal reasons do not always fall the same way.
          (stableHash(`${opts.key}|${s.driverId}|${run.raceNumber}|${c.kind}`) % 7) / 10,
      }));
      cands.sort((a, b) => b.salience - a.salience);
      let reason: WireLine | null = null;
      for (const c of cands) {
        const occ = used.get(`${s.driverId}|${c.kind}`) ?? 0;
        reason = say(`power-${c.kind}`, c.templates, c.slots, { key: `${opts.key}|${s.driverId}`, occurrence: occ, driverIds: [s.driverId] });
        if (reason) {
          used.set(`${s.driverId}|${c.kind}`, occ + 1);
          recent.set(s.driverId, [...mine.filter((m) => run.raceNumber - m.week < POWER_FORMULA.memoryWeeks), { kind: c.kind, week: run.raceNumber }]);
          break;
        }
      }
      return {
        rank: k + 1,
        driverId: s.driverId,
        name: s.name,
        power: Math.round(s.power * 10) / 10,
        prevRank,
        move,
        reason: reason ?? say('power-none', ['In the mix.'], {}, { key: s.driverId })!,
      };
    });
    weeks.push({ raceNumber: run.raceNumber, rows });
    prev = new Map(scores.map((s, k) => [s.driverId, k + 1]));
  }
  return weeks;
}
